import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { djangoUrls } from '../../config/urls';
import { useAuth } from '../../contexts/AuthContext';
import authService, { getLoggedInUser, updateUserData } from '../../utils/authService';
import { useClient } from '../../contexts/ClientContext';
import { resolveClientLogoDisplayUrl } from '../../utils/clientBranding';
import { useFeatures } from '../../contexts/FeaturesContext';
import NavLink from '../NavLink/NavLink';
import api from '../../utils/api';
import './Header.css';

const DEFAULT_LOGO_URL = '/static/logo.png';

const Header = () => {
  const { companyName, logo: clientLogo, tagline, header_tab: headerTabRaw, disableThemeChange, is_super: isSuperClient } = useClient();
  const brandName = companyName || 'Seahire';
  const brandTagline = tagline || 'Maritime Hiring Platform';
  const { hasFeature } = useFeatures();
  const logoUrl = resolveClientLogoDisplayUrl(clientLogo) || DEFAULT_LOGO_URL;
  const showThemeToggle = disableThemeChange !== true;
  const showHr = hasFeature('hr');
  const showPing = hasFeature('ping');
  const showClassroom = hasFeature('classroom');

  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('default');
  const { isLoggedIn } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileProfileImgError, setMobileProfileImgError] = useState(false);

  // Parse header tabs from client context
  const headerTabs = React.useMemo(() => {
    try {
      if (!headerTabRaw) return [];
      if (Array.isArray(headerTabRaw)) return headerTabRaw;
      if (typeof headerTabRaw === 'string') return JSON.parse(headerTabRaw);
      return [];
    } catch (e) {
      console.error('Failed to parse header tabs:', e);
      return [];
    }
  }, [headerTabRaw]);

  // Get current user from auth utility (localStorage); map API fields: first_name, last_name, pic, role
  const rawUser = getLoggedInUser();
  const userAccess = rawUser && rawUser.access != null ? Number(rawUser.access) : null;
  // Manage hidden only for "no role" (access missing/null) or role=9 (Candidate).
  const showManage = !!rawUser && rawUser.access != null && String(rawUser.access) !== "9";
  // Admin panel visible for any user with access other than 9.
  const showAdminPanel = showManage;
  // Client panel remains limited to true admins (access==1) + super client.
  const showClientPanel = userAccess === 1 && !!isSuperClient;
  const currentUser = rawUser
    ? {
        name: [rawUser.first_name, rawUser.last_name].filter(Boolean).join(' ').trim() || rawUser.email || 'User',
        role: rawUser.role || 'User',
        avatar: rawUser.pic || rawUser.image || null
      }
    : { name: 'User', role: '', avatar: null };
  const hasValidMobilePic = currentUser.avatar && currentUser.avatar.trim() && !mobileProfileImgError;

  const fetchNotifications = useCallback(async () => {
    if (!isLoggedIn) return;
    setNotificationsLoading(true);
    try {
      const response = await api.get('/api/notifications?limit=8');
      const data = await response.json();
      if (data.success) {
        const raw = data.data || [];
        const mapped = raw.map((n) => ({
          ...n,
          title: n.title ?? n.subject ?? '',
          message: n.message ?? n.body ?? ''
        }));
        setNotifications(mapped);
        const unread = (data.counts && typeof data.counts.unread === 'number')
          ? data.counts.unread
          : (typeof data.count === 'number' ? data.count : 0);
        setUnreadNotifications(unread);
        updateUserData({ ncount: unread });
      }
    } catch (error) {
      console.error('Failed to fetch header notifications', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [isLoggedIn]);

  // Theme and notification badge from localStorage (ncount); API only when ncount missing or 0)
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'default';
    setCurrentTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
    if (isLoggedIn) {
      const user = getLoggedInUser();
      const ncount = user && typeof user.ncount === 'number' ? user.ncount : null;
      if (ncount != null && ncount > 0) {
        setUnreadNotifications(ncount);
      } else {
        fetchNotifications();
      }
    }
  }, [isLoggedIn, fetchNotifications]);

  useEffect(() => {
    setMobileProfileImgError(false);
  }, [currentUser?.avatar]);

  const toggleTheme = () => {
    const themes = ['default', 'dark', 'light'];
    const currentIndex = themes.indexOf(currentTheme);
    const newTheme = themes[(currentIndex + 1) % themes.length];

    setCurrentTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
    Cookies.remove('cv'); // Invalidate cache so localStorage client is refreshed on next load
  };

  const toggleMobileMenu = () => {
    setMobileMenuVisible(!mobileMenuVisible);
    document.body.style.overflow = !mobileMenuVisible ? 'hidden' : '';
    // Reset dropdowns when closing menu
    if (mobileMenuVisible) {
      setActiveMobileDropdown(null);
    }
  };

  const toggleNotifications = () => {
    if (!notificationsVisible) {
      fetchNotifications();
    }
    setNotificationsVisible(!notificationsVisible);
    document.body.style.overflow = !notificationsVisible ? 'hidden' : '';
  };

  const closeNotifications = () => {
    setNotificationsVisible(false);
    document.body.style.overflow = '';
  };

  const toggleMobileDropdown = (dropdown) => {
    if (activeMobileDropdown === dropdown) {
      setActiveMobileDropdown(null);
    } else {
      setActiveMobileDropdown(dropdown);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/api/notifications/read', { all: true });
      setUnreadNotifications(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
      updateUserData({ ncount: 0 });
    } catch (error) {
      console.error('Error marking all as read', error);
    }
  };

  const NOTIFICATION_ICONS = {
    connection: 'fa-handshake',
    endorsement: 'fa-thumbs-up',
    job: 'fa-briefcase',
    application: 'fa-file-alt',
    message: 'fa-comment-alt',
    event: 'fa-calendar-check',
    certification: 'fa-certificate',
    profile: 'fa-eye',
    test: 'fa-clipboard-check',
    group: 'fa-users',
    system: 'fa-bell',
    security: 'fa-shield-alt'
  };

  const getNotificationActions = (notification) => {
    switch (notification.type) {
      case 'connection':
        return [
          { text: 'Accept', icon: 'fa-check', class: 'btn-primary', action: 'accept' },
          { text: 'Decline', icon: 'fa-times', class: 'btn-outline-secondary', action: 'decline' }
        ];
      case 'endorsement':
        return [
          { text: 'Thank', icon: 'fa-thumbs-up', class: 'btn-primary', action: 'thank' }
        ];
      case 'job':
        return [
          { text: 'View Job', icon: 'fa-eye', class: 'btn-primary', action: 'view' },
          { text: 'Save', icon: 'fa-bookmark', class: 'btn-outline-primary', action: 'save' }
        ];
      case 'message':
        return [
          { text: 'Reply', icon: 'fa-reply', class: 'btn-primary', action: 'reply' }
        ];
      case 'event':
        return [
          { text: 'Add to Calendar', icon: 'fa-calendar-plus', class: 'btn-primary', action: 'calendar' }
        ];
      case 'certification':
        if (notification.title && notification.title.toLowerCase().includes('expir')) {
          return [{ text: 'Renew Now', icon: 'fa-sync', class: 'btn-primary', action: 'renew' }];
        }
        return [{ text: 'View Certificate', icon: 'fa-eye', class: 'btn-primary', action: 'view' }];
      case 'application':
        return [{ text: 'View Status', icon: 'fa-eye', class: 'btn-primary', action: 'view' }];
      default:
        return [];
    }
  };

  const renderNotificationIcon = (notification) => {
    switch (notification.type) {
      case 'connection':
      case 'endorsement':
      case 'message':
      case 'network':
        if (notification.sender && notification.sender.avatar) {
          return (
            <div className="header-notification-icon notification-avatar">
              <img src={notification.sender.avatar} alt={notification.sender.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            </div>
          );
        }
        break;
      case 'job':
      case 'profile_view':
        if (notification.company) {
          const hasLogo = notification.company.logo && notification.company.logo.trim();
          return (
            <div className="header-notification-icon notification-company-logo header-notification-company-logo-wrapper">
              {hasLogo ? (
                <img
                  src={notification.company.logo}
                  alt={notification.company.name}
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fb = e.target.nextElementSibling;
                    if (fb) fb.classList.add('show');
                  }}
                />
              ) : null}
              <div className={`header-notification-company-default ${!hasLogo ? 'show' : ''}`}>
                <i className="fas fa-building"></i>
              </div>
            </div>
          );
        }
        break;
      default:
        break;
    }

    const iconClass = NOTIFICATION_ICONS[notification.type] || 'fa-bell';
    return (
      <div className="header-notification-icon">
        <i className={`fas ${iconClass}`}></i>
      </div>
    );
  };

  // Scroll effects
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset > 50;
      setNavbarScrolled(scrolled);
      setShowBackToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNotificationAction = (notification, action) => {
    // Placeholder for handle action
    console.log('Action:', action, 'on notification:', notification);
  };

  // Normalize header_tab: New format { tab_name, sub_tabs } or legacy array
  const getHeaderTabData = (headerTab) => {
    if (!headerTab) return { tab_name: 'More', sub_tabs: [] };
    if (Array.isArray(headerTab)) {
      // Legacy format: convert array to new structure
      return {
        tab_name: 'Pages',
        sub_tabs: headerTab.map(item => ({
          label: typeof item === 'string' ? item : (item.label || ''),
          url: typeof item === 'string' ? '#' : (item.url || item.slug ? `/p/${item.slug}/` : '#')
        })).filter(item => item.label)
      };
    }
    // New format: { tab_name, sub_tabs }
    return headerTab;
  };

  const headerTabData = getHeaderTabData(headerTabRaw);
  const headerTabName = headerTabData.tab_name || 'More';
  const headerTabItems = headerTabData.sub_tabs || [];

  return (
    <>
      <nav className={`navbar header-navbar navbar-expand-lg navbar-dark ${navbarScrolled ? 'scrolled' : ''}`}>
        <div className="container header-container">
          <button className="navbar-toggler header-navbar-toggler" type="button" onClick={toggleMobileMenu}>
            <span className="header-toggler-bar"></span>
            <span className="header-toggler-bar"></span>
            <span className="header-toggler-bar"></span>
          </button>

          <div className="header-brand-group">
            <NavLink className="navbar-brand header-navbar-brand" to={djangoUrls.homepage} aria-label={brandName}>
              <img src={logoUrl} alt="" className="header-logo" aria-hidden="true" />
              <span className="header-brand-text">
                <span className="header-brand-name">{brandName}</span>
                <span className="header-brand-tagline">{brandTagline}</span>
              </span>
            </NavLink>
            {/* Workplace - only when at least one of HR / Ping / Classroom is enabled */}
            {(showHr || showPing || showClassroom) && (
              <div className="header-workplace-wrap d-none d-lg-block">
                <ul className="navbar-nav header-navbar-nav">
                  <li className="nav-item header-nav-item dropdown">
                    <button className="nav-link header-nav-link dropdown-toggle header-dropdown-toggle header-workplace-toggle" id="workplaceDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                      <i className="fas fa-briefcase header-workplace-icon"></i>
                      <span>Workplace</span>
                      <i className="fas fa-chevron-down header-workplace-caret"></i>
                    </button>
                    <ul className="dropdown-menu header-dropdown-menu" aria-labelledby="workplaceDropdown">
                      {showHr && <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.hr_test}><i className="fas fa-user-tie me-2"></i> HR Portal</NavLink></li>}
                      {showPing && <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.ping_test}><i className="fas fa-comments me-2"></i> Ping</NavLink></li>}
                      {showClassroom && <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.classroom}><i className="fas fa-chalkboard-teacher me-2"></i> Classroom</NavLink></li>}
                    </ul>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Mobile: notifications only — theme & profile live in the sidebar */}
          <div className="header-mobile-nav-icons">
            {isLoggedIn && (
              <div className="notification-container">
                <div className="header-notification-bell" onClick={toggleNotifications}>
                  <i className="fas fa-bell"></i>
                  {unreadNotifications > 0 && (
                    <span className="header-notification-badge">{unreadNotifications}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="collapse collapse navbar-collapse header-navbar-collapse" id="navbarNav">
            <ul className="navbar-nav header-navbar-nav header-nav-center ms-auto">
              {/* Manage - independent placement */}
              {showManage && (
                <div className="header-manage-wrapper d-none d-lg-flex">
                  <ul className="navbar-nav">
                    <li className="nav-item header-nav-item dropdown header-manage-container">
                      <button className="nav-link header-nav-link dropdown-toggle header-dropdown-toggle header-manage-toggle" id="manageDropdownDesktop" data-bs-toggle="dropdown" aria-expanded="false">
                        Manage
                        <i className="fas fa-chevron-down"></i>
                      </button>
                      <ul className="dropdown-menu header-dropdown-menu dropdown-menu-end" aria-labelledby="manageDropdownDesktop">
                        {showAdminPanel && (
                          <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.admin_test}><i className="fas fa-user-shield me-2"></i> Admin Panel</NavLink></li>
                        )}
                        {showClientPanel && (
                          <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.client_test}><i className="fas fa-users me-2"></i> Client Panel</NavLink></li>
                        )}
                      </ul>
                    </li>
                  </ul>
                </div>
              )}

              {/* Job Listings Dropdown */}
              <li className="nav-item header-nav-item dropdown">
                <button className="nav-link header-nav-link dropdown-toggle header-dropdown-toggle" id="jobsDropdown">
                  Job Listings
                </button>
                <ul className="dropdown-menu header-dropdown-menu" aria-labelledby="jobsDropdown">
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.job_search}><i className="fas fa-search me-2"></i> Job Search</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.all_jobs}><i className="fas fa-briefcase me-2"></i> All Jobs</NavLink></li>
                  {isLoggedIn && (
                    <>
                      <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.recommended_jobs}><i className="fas fa-thumbtack me-2"></i> Recommended Jobs</NavLink></li>
                      <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.saved_jobs}><i className="fas fa-bookmark me-2"></i> Saved Jobs</NavLink></li>
                    </>
                  )}
                </ul>
              </li>

              {/* Employers Dropdown */}
              <li className="nav-item header-nav-item dropdown">
                <button className="nav-link header-nav-link dropdown-toggle header-dropdown-toggle" id="employersDropdown">
                  Employers
                </button>
                <ul className="dropdown-menu header-dropdown-menu" aria-labelledby="employersDropdown">
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.request_demo}><i className="fas fa-chalkboard-teacher me-2"></i> Take a Demo</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.recruitment_solutions}><i className="fas fa-users me-2"></i> Recruitment Solutions</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.branded_company_page}><i className="fas fa-building me-2"></i> Branded Company Page</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.request_portal}><i className="fas fa-file-signature me-2"></i> Get Your Portal</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.manage_employees_info}><i className="fas fa-user-tie me-2"></i> Manage Employees</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.support_services}><i className="fas fa-headset me-2"></i> Employer Support</NavLink></li>
                </ul>
              </li>

              {/* Career Toolkit Dropdown */}
              <li className="nav-item header-nav-item dropdown">
                <button className="nav-link header-nav-link dropdown-toggle header-dropdown-toggle" id="resourcesDropdown">
                  Career Toolkit
                </button>
                <ul className="dropdown-menu header-dropdown-menu" aria-labelledby="resourcesDropdown">
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.resume_tips}><i className="fas fa-file-alt me-2"></i> Resume & Interview Tips</NavLink></li>
                  <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.interview_preparation}><i className="fas fa-user-check me-2"></i> Interview Preparation</NavLink></li>
                </ul>
              </li>

              {/* Pages dropdown from meta API (header_tab) - between Career Toolkit and notification/login */}
              {headerTabItems.length > 0 && (
                <li className="nav-item header-nav-item dropdown">
                  <button className="nav-link header-nav-link dropdown-toggle header-dropdown-toggle" id="pagesDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                    {headerTabName}
                  </button>
                  <ul className="dropdown-menu header-dropdown-menu" aria-labelledby="pagesDropdown">
                    {headerTabItems.map((item, idx) => {
                      // Check if it's truly external (not same domain)
                      // resolve_page_url returns absolute URLs but we want to treat them as internal if they are on this site
                      const isExternal = (item.url?.startsWith('http://') || item.url?.startsWith('https://')) &&
                        !item.url.includes(window.location.host);

                      return (
                        <li key={idx}>
                          {isExternal ? (
                            <a className="dropdown-item header-dropdown-item" href={item.url} target="_blank" rel="noopener noreferrer">
                              {item.label}
                            </a>
                          ) : (
                            <NavLink className="dropdown-item header-dropdown-item" to={item.url || '#'}>
                              {item.label}
                            </NavLink>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              )}
            </ul>

            {/* Desktop Icons */}
            <div className="header-desktop-nav-icons d-none d-lg-flex align-items-center ms-4">
              {!isLoggedIn ? (
                <div className="header-auth-buttons me-3">
                  <NavLink className="btn header-btn-login" to={djangoUrls.login}>Login</NavLink>
                  <NavLink className="btn header-btn-signup" to={djangoUrls.signup}>Sign Up</NavLink>
                </div>
              ) : (
                <>
                  {/* Custom Header Tabs */}
                  {headerTabs.length > 0 && (
                    <div className="d-flex align-items-center">
                      {headerTabs.map((tab, index) => (
                        <NavLink key={`desktop-header-tab-${index}`} className="nav-link header-nav-link me-3" to={tab.url || '#'}>
                          {tab.label}
                        </NavLink>
                      ))}
                    </div>
                  )}

                  <div className="notification-container me-3">
                    <div className="header-notification-bell" onClick={toggleNotifications}>
                      <i className="fas fa-bell"></i>
                      {unreadNotifications > 0 && (
                        <span className="header-notification-badge">{unreadNotifications}</span>
                      )}
                    </div>
                  </div>
                  <li className="nav-item header-nav-item dropdown header-profile-container">
                    <button className="header-profile-icon nav-link header-nav-link dropdown-toggle header-dropdown-toggle" id="profileDropdown">
                      <i className="fas fa-user"></i>
                    </button>
                    <ul className="dropdown-menu header-dropdown-menu dropdown-menu-end" aria-labelledby="profileDropdown">
                      <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.profile}><i className="fas fa-user me-2"></i> My Profile</NavLink></li>
                      <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.upcoming_interviews}><i className="fas fa-calendar-alt me-2"></i> Upcoming Interviews</NavLink></li>
                      <li><NavLink className="dropdown-item header-dropdown-item" to={djangoUrls.application_timeline}><i className="fas fa-tasks me-2"></i> Application Timeline</NavLink></li>
                      <li><hr className="dropdown-divider header-dropdown-divider" /></li>
                      <li><button className="dropdown-item header-dropdown-item" onClick={() => authService.logout()}><i className="fas fa-sign-out-alt me-2"></i> Logout</button></li>
                    </ul>
                  </li>
                </>
              )}

              {showThemeToggle && (
                <div className="header-theme-toggle-box">
                  <button className="header-theme-toggle" onClick={toggleTheme}>
                    <i className={`fas ${currentTheme === 'dark' ? 'fa-sun' :
                      currentTheme === 'default' ? 'fa-palette' : 'fa-moon'
                      }`}></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Notification Panel */}
      <div className={`header-notification-panel ${notificationsVisible ? 'show' : ''}`}>
        <div className="header-notification-header">
          <h3>Notifications</h3>
          <div>
            <button className="header-mark-all-read" onClick={markAllAsRead}>Mark all as read</button>
            <button className="header-close-notifications" onClick={toggleNotifications}><i className="fas fa-times"></i></button>
          </div>
        </div>
        <div className="header-notification-body">
          {notificationsLoading ? (
            <div className="header-notification-loading">
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="header-notification-empty">
              <i className="far fa-bell-slash"></i>
              <p>No notifications found</p>
            </div>
          ) : (
            <ul className="header-notification-list">
              {notifications.map((notification, index) => {
                const actions = getNotificationActions(notification);
                return (
                  <li key={notification.id ?? index} className={`header-notification-item ${!notification.read ? 'unread' : 'read'}`}>
                    <div className="header-notification-item-content-wrapper">
                      {renderNotificationIcon(notification)}
                      <div className="header-notification-item-content">
                        <div className="header-notification-title">{notification.title}</div>
                        <p className="header-notification-text" dangerouslySetInnerHTML={{ __html: notification.message || '' }}></p>
                        <div className="header-notification-time">
                          <i className="far fa-clock"></i><span>{notification.time}</span>
                        </div>
                        {actions && actions.length > 0 && (
                          <div className="header-notification-actions">
                            {actions.map((action, actionIndex) => (
                              <button
                                key={actionIndex}
                                className="header-notification-action-btn"
                                onClick={() => handleNotificationAction(notification, action)}
                              >
                                {action.text}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="header-notification-footer">
          <NavLink to={djangoUrls.notifications} className="header-notification-view-all" onClick={closeNotifications}>View All Notifications</NavLink>
        </div>
      </div>
      <div
        className={`header-notification-overlay ${notificationsVisible ? 'show' : ''}`}
        onClick={toggleNotifications}
      ></div>

      {/* Mobile Menu */}
      <div className={`header-mobile-menu ${mobileMenuVisible ? 'show' : ''}`}>
        <div className="header-mobile-menu-header d-flex align-items-center justify-content-between">
          <h3 className="mb-0">Menu</h3>
          <div className="header-mobile-menu-header-actions">
            {showThemeToggle && (
              <button
                type="button"
                className="header-mobile-menu-theme-icon"
                onClick={toggleTheme}
                aria-label="Change theme"
                title="Change theme"
              >
                <i
                  className={`fas ${currentTheme === 'dark' ? 'fa-sun' : currentTheme === 'default' ? 'fa-palette' : 'fa-moon'}`}
                  aria-hidden
                />
              </button>
            )}
            <button type="button" className="header-close-menu" onClick={toggleMobileMenu} aria-label="Close menu">
              <i className="fas fa-times" aria-hidden />
            </button>
          </div>
        </div>

        {isLoggedIn && (
          <NavLink
            to={djangoUrls.profile}
            className="header-mobile-menu-profile"
            onClick={toggleMobileMenu}
          >
            <div className="header-mobile-menu-profile-img">
              {hasValidMobilePic ? (
                <img
                  src={currentUser.avatar}
                  alt={currentUser.name}
                  onError={() => setMobileProfileImgError(true)}
                />
              ) : (
                <div className="header-mobile-menu-profile-icon">
                  <i className="fas fa-user"></i>
                </div>
              )}
            </div>
            <div className="header-mobile-menu-profile-info">
              <h4>{currentUser.name}</h4>
              <p>{currentUser.role}</p>
            </div>
          </NavLink>
        )}

        {/* Auth buttons in mobile menu */}
        <div className="header-mobile-auth-buttons" style={{ display: isLoggedIn ? 'none' : 'flex' }}>
          <NavLink className="header-mobile-btn-login" to={djangoUrls.login}>Login</NavLink>
          <NavLink className="header-mobile-btn-signup" to={djangoUrls.signup}>Sign Up</NavLink>
        </div>

        <ul className="header-mobile-menu-nav">
          <li><NavLink to={djangoUrls.homepage} onClick={toggleMobileMenu}><i className="fas fa-home"></i> Home</NavLink></li>

          {isLoggedIn && (
            <>
              <li><NavLink to={djangoUrls.upcoming_interviews} onClick={toggleMobileMenu}><i className="fas fa-calendar-alt"></i> Upcoming Interviews</NavLink></li>
              <li><NavLink to={djangoUrls.application_timeline} onClick={toggleMobileMenu}><i className="fas fa-tasks"></i> Application Timeline</NavLink></li>
            </>
          )}

          {/* Workplace - first in mobile */}
          {(showHr || showPing || showClassroom) && (
            <li className="header-mobile-dropdown-item">
              <div
                className={`header-mobile-dropdown-toggle ${activeMobileDropdown === 'workplace' ? 'show' : ''}`}
                onClick={() => toggleMobileDropdown('workplace')}
              >
                <span><i className="fas fa-building"></i> Workplace</span>
              </div>
              <ul className={`header-mobile-dropdown-menu ${activeMobileDropdown === 'workplace' ? 'show' : ''}`}>
                {showHr && <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.hr_test} onClick={toggleMobileMenu}><i className="fas fa-user-tie me-2"></i> HR Portal</NavLink></li>}
                {showPing && <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.ping_test} onClick={toggleMobileMenu}><i className="fas fa-comments me-2"></i> Ping</NavLink></li>}
                {showClassroom && <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.classroom} onClick={toggleMobileMenu}><i className="fas fa-chalkboard-teacher me-2"></i> Classroom</NavLink></li>}
              </ul>
            </li>
          )}

          {/* Manage - second in mobile */}
          {showManage && (
            <li className="header-mobile-dropdown-item">
              <div
                className={`header-mobile-dropdown-toggle ${activeMobileDropdown === 'manage' ? 'show' : ''}`}
                onClick={() => toggleMobileDropdown('manage')}
              >
                <span><i className="fas fa-cog"></i> Manage</span>
              </div>
              <ul className={`header-mobile-dropdown-menu ${activeMobileDropdown === 'manage' ? 'show' : ''}`}>
                {showAdminPanel && (
                  <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.admin_test} onClick={toggleMobileMenu}><i className="fas fa-user-shield me-2"></i> Admin Panel</NavLink></li>
                )}
                {showClientPanel && (
                  <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.client_test} onClick={toggleMobileMenu}><i className="fas fa-users me-2"></i> Client Panel</NavLink></li>
                )}
              </ul>
            </li>
          )}

          {/* Job Listings Dropdown */}
          <li className="header-mobile-dropdown-item">
            <div
              className={`header-mobile-dropdown-toggle ${activeMobileDropdown === 'jobs' ? 'show' : ''}`}
              onClick={() => toggleMobileDropdown('jobs')}
            >
              <span><i className="fas fa-briefcase"></i> Job Listings</span>
            </div>
            <ul className={`header-mobile-dropdown-menu ${activeMobileDropdown === 'jobs' ? 'show' : ''}`}>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.job_search} onClick={toggleMobileMenu}><i className="fas fa-search me-2"></i> Job Search</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.all_jobs} onClick={toggleMobileMenu}><i className="fas fa-briefcase me-2"></i> All Jobs</NavLink></li>
              {isLoggedIn && (
                <>
                  <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.recommended_jobs} onClick={toggleMobileMenu}><i className="fas fa-thumbtack me-2"></i> Recommended Jobs</NavLink></li>
                  <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.saved_jobs} onClick={toggleMobileMenu}><i className="fas fa-bookmark me-2"></i> Saved Jobs</NavLink></li>
                </>
              )}
            </ul>
          </li>

          {/* Employers Dropdown */}
          <li className="header-mobile-dropdown-item">
            <div
              className={`header-mobile-dropdown-toggle ${activeMobileDropdown === 'employers' ? 'show' : ''}`}
              onClick={() => toggleMobileDropdown('employers')}
            >
              <span><i className="fas fa-building"></i> Employers</span>
            </div>
            <ul className={`header-mobile-dropdown-menu ${activeMobileDropdown === 'employers' ? 'show' : ''}`}>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.request_demo} onClick={toggleMobileMenu}><i className="fas fa-chalkboard-teacher me-2"></i> Take a Demo</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.recruitment_solutions} onClick={toggleMobileMenu}><i className="fas fa-users me-2"></i> Recruitment Solutions</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.branded_company_page} onClick={toggleMobileMenu}><i className="fas fa-building me-2"></i> Branded Company Page</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.request_portal} onClick={toggleMobileMenu}><i className="fas fa-file-signature me-2"></i> Get Your Portal</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.manage_employees_info} onClick={toggleMobileMenu}><i className="fas fa-user-tie me-2"></i> Manage Employees</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.support_services} onClick={toggleMobileMenu}><i className="fas fa-headset me-2"></i> Employer Support</NavLink></li>
            </ul>
          </li>

          {/* Career Toolkit Dropdown */}
          <li className="header-mobile-dropdown-item">
            <div
              className={`header-mobile-dropdown-toggle ${activeMobileDropdown === 'resources' ? 'show' : ''}`}
              onClick={() => toggleMobileDropdown('resources')}
            >
              <span><i className="fas fa-book"></i> Career Toolkit</span>
            </div>
            <ul className={`header-mobile-dropdown-menu ${activeMobileDropdown === 'resources' ? 'show' : ''}`}>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.resume_tips} onClick={toggleMobileMenu}><i className="fas fa-file-alt me-2"></i> Resume & Interview Tips</NavLink></li>
              <li><NavLink className="header-mobile-dropdown-item" to={djangoUrls.interview_preparation} onClick={toggleMobileMenu}><i className="fas fa-user-check me-2"></i> Interview Preparation</NavLink></li>
            </ul>
          </li>

          {/* Pages (from header_tab / meta API) - at bottom of sidebar */}
          {headerTabItems.length > 0 && (
            <li className="header-mobile-dropdown-item">
              <div
                className={`header-mobile-dropdown-toggle ${activeMobileDropdown === 'pages' ? 'show' : ''}`}
                onClick={() => toggleMobileDropdown('pages')}
              >
                <span><i className="fas fa-file-alt"></i> {headerTabName}</span>
              </div>
              <ul className={`header-mobile-dropdown-menu ${activeMobileDropdown === 'pages' ? 'show' : ''}`}>
                {headerTabItems.map((item, idx) => {
                  const isExternal = (item.url?.startsWith('http://') || item.url?.startsWith('https://')) &&
                    !item.url.includes(window.location.host);
                  return (
                    <li key={idx}>
                      {isExternal ? (
                        <a className="header-mobile-dropdown-item" href={item.url} target="_blank" rel="noopener noreferrer" onClick={toggleMobileMenu}>
                          {item.label}
                        </a>
                      ) : (
                        <NavLink className="header-mobile-dropdown-item" to={item.url || '#'} onClick={toggleMobileMenu}>
                          {item.label}
                        </NavLink>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          )}

          {/* Custom Header Tabs - Mobile */}
          {headerTabs.map((tab, index) => (
            <li key={`mobile-header-tab-${index}`}>
              <NavLink className="header-mobile-dropdown-item" to={tab.url || '#'} onClick={toggleMobileMenu}>
                {tab.label}
              </NavLink>
            </li>
          ))}

          {isLoggedIn && (
            <li><button onClick={(e) => { e.preventDefault(); authService.logout(); }}><i className="fas fa-sign-out-alt me-2"></i> Logout</button></li>
          )}
        </ul>
      </div>
      <div
        className={`header-mobile-menu-overlay ${mobileMenuVisible ? 'show' : ''}`}
        onClick={toggleMobileMenu}
      ></div>

      {/* Back to Top */}
      <button
        className={`header-back-to-top ${showBackToTop ? 'show' : ''}`}
        onClick={scrollToTop}
      >
        <i className="fas fa-arrow-up"></i>
      </button>
    </>
  );
};

export default Header;

